import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileSignature } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h3 className="text-sm font-semibold text-primary uppercase tracking-wide">{title}</h3>
    {children}
  </section>
);

const Field = ({ label, required, children }) => (
  <div>
    <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

export default function ApplicationDialog({
  open, onOpenChange, propertyTitle,
  appData, setAppData,
  leaseData, setLeaseData,
  isDateUnavailable, onSubmit,
}) {
  const setApp = (k, v) => setAppData({ ...appData, [k]: v });
  const setLease = (k, v) => setLeaseData({ ...leaseData, [k]: v });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="sign-lease-btn" size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
          <FileSignature className="w-4 h-4 mr-2" />Apply
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-dialog">
        <DialogHeader>
          <DialogTitle>Rental Application — {propertyTitle}</DialogTitle>
          <DialogDescription>Complete this application to submit your rental request. Fields marked with * are required.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          <Section title="Personal Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full Legal Name" required>
                <Input data-testid="app-full-name" value={appData.full_legal_name} onChange={(e) => setApp('full_legal_name', e.target.value)} />
              </Field>
              <Field label="Date of Birth" required>
                <Input data-testid="app-dob" type="date" value={appData.date_of_birth} onChange={(e) => setApp('date_of_birth', e.target.value)} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Current Address" required>
                  <Input data-testid="app-current-address" placeholder="Street, City, State, Zip" value={appData.current_address} onChange={(e) => setApp('current_address', e.target.value)} />
                </Field>
              </div>
            </div>
          </Section>

          <Section title="Employment & Income">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Employer" required>
                <Input data-testid="app-employer" value={appData.employer} onChange={(e) => setApp('employer', e.target.value)} />
              </Field>
              <Field label="Job Title">
                <Input data-testid="app-job-title" value={appData.job_title} onChange={(e) => setApp('job_title', e.target.value)} />
              </Field>
              <Field label="Gross Monthly Income (USD)" required>
                <Input data-testid="app-monthly-income" type="number" min="0" step="100" value={appData.monthly_income} onChange={(e) => setApp('monthly_income', e.target.value)} placeholder="6500" />
              </Field>
              <Field label="Employed Since">
                <Input data-testid="app-employed-since" type="date" value={appData.employment_since} onChange={(e) => setApp('employment_since', e.target.value)} />
              </Field>
            </div>
          </Section>

          <Section title="Rental History">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Current Landlord Name">
                <Input data-testid="app-cur-landlord-name" value={appData.current_landlord_name} onChange={(e) => setApp('current_landlord_name', e.target.value)} />
              </Field>
              <Field label="Current Landlord Phone">
                <Input data-testid="app-cur-landlord-phone" value={appData.current_landlord_phone} onChange={(e) => setApp('current_landlord_phone', e.target.value)} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Reason for Moving">
                  <Input data-testid="app-reason" value={appData.reason_for_moving} onChange={(e) => setApp('reason_for_moving', e.target.value)} />
                </Field>
              </div>
            </div>
          </Section>

          <Section title="Personal Reference">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Name" required>
                <Input data-testid="app-ref-name" value={appData.reference_name} onChange={(e) => setApp('reference_name', e.target.value)} />
              </Field>
              <Field label="Relationship">
                <Input data-testid="app-ref-rel" value={appData.reference_relationship} onChange={(e) => setApp('reference_relationship', e.target.value)} />
              </Field>
              <Field label="Phone" required>
                <Input data-testid="app-ref-phone" value={appData.reference_phone} onChange={(e) => setApp('reference_phone', e.target.value)} />
              </Field>
            </div>
          </Section>

          <Section title="Emergency Contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name" required>
                <Input data-testid="app-emerg-name" value={appData.emergency_contact_name} onChange={(e) => setApp('emergency_contact_name', e.target.value)} />
              </Field>
              <Field label="Phone" required>
                <Input data-testid="app-emerg-phone" value={appData.emergency_contact_phone} onChange={(e) => setApp('emergency_contact_phone', e.target.value)} />
              </Field>
            </div>
          </Section>

          <Section title="Lease Preferences">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Lease Duration">
                <Select value={leaseData.lease_months} onValueChange={(v) => setLease('lease_months', v)}>
                  <SelectTrigger data-testid="lease-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Months</SelectItem>
                    <SelectItem value="6">6 Months</SelectItem>
                    <SelectItem value="12">12 Months</SelectItem>
                    <SelectItem value="24">24 Months</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div>
                <Field label="Move-in Date" required>
                  <Input data-testid="lease-start-date-input" type="date" value={leaseData.start_date} onChange={(e) => setLease('start_date', e.target.value)} />
                </Field>
                {leaseData.start_date && isDateUnavailable(new Date(leaseData.start_date)) && (
                  <p className="text-xs text-destructive mt-1">This date is unavailable</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <Field label="Down Payment (Optional)">
                  <Input data-testid="lease-downpayment-input" type="number" min="0" step="10" value={leaseData.down_payment} onChange={(e) => setLease('down_payment', e.target.value)} placeholder="0" />
                </Field>
                <p className="text-xs text-muted-foreground mt-1">Prepay any amount to reduce future rent. Platform fee: 2% of down payment.</p>
              </div>
            </div>
          </Section>

          <Section title="Consent & Signature">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                data-testid="app-consent-bg-check"
                type="checkbox"
                checked={appData.consent_background_check}
                onChange={(e) => setApp('consent_background_check', e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-border"
              />
              <span className="text-sm text-muted-foreground">
                I consent to a background and credit check as part of this rental application, and I certify that all information provided is true and accurate to the best of my knowledge. <span className="text-destructive">*</span>
              </span>
            </label>
            <Field label="Your Signature (type full legal name)" required>
              <Input data-testid="lease-signature-input" placeholder="e.g., Jane Doe" value={leaseData.tenant_signature} onChange={(e) => setLease('tenant_signature', e.target.value)} className="font-serif italic" />
            </Field>
          </Section>

          <Button data-testid="lease-submit-btn" onClick={onSubmit} className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
            Submit Application
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
